import { Card, Typography, Statistic, Space, Col, Row, Alert } from 'antd';
import { palette } from '@shipium-corp/shipium-ui-theme';
import { createUseStyles } from 'react-jss';
import type { Range } from './SimulationChoropleth';
import type { ChoroplethCountyRecord, ChoroplethStateRecord } from '../../store/simulation/types';
import { useCarriersFiltered } from '../../hooks/queries/useCarriersFiltered';

const { Title } = Typography;

const useStyles = createUseStyles({
  label: {
    border: '1px solid',
  },
  sidebarTitle: {
    textAlign: 'center',
    marginBottom: '0px',
  },
  population: {
    marginLeft: '5px',
  },
  sidebarSubTitle: {
    textAlign: 'center',
    marginTop: '0px !important',
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '3px',
    top: '100px',
    width: '350px',
    boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1);',
    position: 'absolute',
    zIndex: 1,
    left: '8px',
  },
  sidebarRow: {
    padding: '0px 10px 0px 0px',
  },
  key: {
    borderRadius: '50%',
    display: 'inline-block',
    height: '10px',
    marginRight: '10px',
    width: '10px',
  },
  stateHeading: {
    textAlign: 'center',
    marginBottom: '-36px',
    marginLeft: '-20px',
    marginTop: '20px',
  },
  legend: {
    marginTop: '-20px',
  },
  countyHeading: {
    textAlign: 'center',
  },
  carrierSummaryTable: {
    width: '100%',
    marginTop: '10px',
    borderTop: '1px solid #f0f0f0',
    '& td': {
      padding: '3px 5px',
    },
    '& td:nth-child(1)': {
      borderRight: '1px solid #f0f0f0',
    },
    '& td:nth-child(2)': {
      textAlign: 'right',
      verticalAlign: 'top',
    },
    '& tr:first-child': {
      fontWeight: 'bold',
    },
  },
  carrierListWrapper: {
    marginTop: '10px',
  },
});

export interface SimulationChoroplethSidebarProps {
  ranges?: Range[];
  activeState?: ChoroplethStateRecord;
  activeCounty?: ChoroplethCountyRecord;
}

export const SimulationChoroplethSidebar = ({
  ranges,
  activeState,
  activeCounty,
}: SimulationChoroplethSidebarProps) => {
  const classes = useStyles();
  const carrierMap = useCarriersFiltered();

  const summaryBlock = (record: ChoroplethStateRecord | ChoroplethCountyRecord) => {
    return (
      <>
        <Row>
          <Col span={12}>
            <Statistic
              title={'Shipments'}
              value={record?.count}
              precision={0}
              valueStyle={{ color: palette.Grey.dark }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title={'Avg Time in Transit'}
              value={record?.primaryCarrierTimeInTransitDays ?? 'N/A'}
              suffix={
                record?.primaryCarrierTimeInTransitDays
                  ? record?.primaryCarrierTimeInTransitDays === 1
                    ? 'day'
                    : 'days'
                  : ''
              }
              precision={0}
              valueStyle={{ color: palette.Grey.dark }}
            />
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Statistic
              title={'Total Cost'}
              value={record?.totalLabelCost}
              prefix={'$'}
              precision={0}
              valueStyle={{ color: palette.Grey.dark }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title={'Average Cost'}
              value={record?.averageLabelCost}
              prefix={'$'}
              precision={2}
              valueStyle={{ color: palette.Grey.dark }}
            />
          </Col>
        </Row>
      </>
    );
  };

  const carrierList = (record: ChoroplethStateRecord | ChoroplethCountyRecord) => {
    return (
      <Row className={classes.carrierListWrapper}>
        <Col span={24}>
          <span className="ant-statistic-title">Carrier Shipments</span>
          <table className={classes.carrierSummaryTable}>
            {Object.values(record?.carriers)
              .sort((a, b) => b.count - a.count)
              .map((carrier, index) => {
                const label = carrierMap[carrier.carrier]?.name || carrier.carrier;
                const color = ranges?.find((row) => row.key === carrier.carrier)?.color;
                return (
                  <tr key={index}>
                    <td>
                      <span className={classes.key} style={{ backgroundColor: `#${color}`, opacity: 0.75 }}></span>
                      {label}
                    </td>
                    <td>{carrier.count.toLocaleString()}</td>
                  </tr>
                );
              })}
          </table>
        </Col>
      </Row>
    );
  };

  return (
    <>
      {!activeState && !activeCounty && (
        <Card size={'small'} type="inner" style={{ height: '100%' }}>
          <Alert
            type={'info'}
            showIcon={true}
            description={'Hover or click on a state or county to view details.'}
            message={'Select a State or County'}
          />
        </Card>
      )}
      {activeState && (
        <Card size={'small'} type="inner" style={{ height: '100%' }}>
          <>
            <Title className={classes.sidebarTitle} level={4}>
              {activeState.stateName}
            </Title>
            {summaryBlock(activeState)}
            {carrierList(activeState)}
          </>
        </Card>
      )}
      {activeCounty && (
        <Card size={'small'} type="inner" style={{ height: '100%' }}>
          <>
            <Title className={classes.sidebarTitle} level={4}>
              {activeCounty.countyName} {activeCounty.stateName}
            </Title>
            <Title className={classes.sidebarSubTitle} level={5}>
              <Space>
                <small>Population Density: {Math.round(activeCounty.density).toLocaleString()} / per sq. km.</small>
              </Space>
            </Title>
            {summaryBlock(activeCounty)}
            {carrierList(activeCounty)}
          </>
        </Card>
      )}
    </>
  );
};
